import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useModuleSettings } from '../../context/adminSettingsContext'
import { hasPermission } from '../../common/permissions'
import Swal from 'sweetalert2'
import { selectGlobalLoading } from '../../store/selectors/loadingSelector'
import { downloadEarningReport, earningListing } from '../../apis/apiHandler'
import DateRangePicker from "react-bootstrap-daterangepicker";
import Globals from '../../common/global'
import { exportExcelFile, getCardName } from '../../common/utils'
import { allEarnings } from '../../slice/earningSlice'

const Earnings = () => {
  const dispatch = useDispatch();
  const { earningList } = useSelector(state => state.earning);
  const isLoading = useSelector(selectGlobalLoading);
  const navigate = useNavigate();

  const {
    page,
    setPage,
    limit,
    setLimit,
  } = useModuleSettings();

  console.log("earningList", earningList?.data);

  const [statusFilter, setStatusFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDebounce, setSearchDebounce] = useState('')
  const [filters, setFilters] = useState({
    rangDate: {
      start_date: '',
      end_date: '',
    },
  })

  const setDates = (e, { startDate, endDate }) => {
    setFilters((prev) => ({
      ...prev,
      rangDate: {
        start_date: moment(startDate)._i !== 0 ? moment(startDate).format('YYYY-MM-DD') : '',
        end_date: moment(endDate)._i !== 0 ? moment(endDate).format('YYYY-MM-DD') : ''
      },
    }));
  }

  const handleResetFilters = () => {
    setSearchQuery('')
    setStatusFilter('')
    setFilters({ rangDate: { start_date: '', end_date: '' } })
    setPage(1)
  }

  const isAnyFilterActive = searchQuery || statusFilter || filters?.rangDate?.start_date

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery)
      setPage(1)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    dispatch(allEarnings({
      page,
      limit,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      search: searchDebounce || undefined,
      date_from: filters?.rangDate?.start_date ?? null,
      date_to: filters?.rangDate?.end_date ?? null,
    }))
  }, [dispatch, page, limit, statusFilter, searchDebounce, filters])

  const handleView = (earningId) => {
    navigate('/earnings/view', {
      state: { earningId }
    })
  }

  const columns = [
    {
      name: 'Sr No',
      width: '80px',
      selector: (_, index) =>
        ((earningList?.paging?.page_no || 1) - 1) * limit + index + 1,
    },
    {
      name: 'First Name',
      selector: row => row.first_name,
      sortable: true,
      minWidth: '160px',
      cell: row => (
        <span style={{ fontWeight: '600', color: '#495057' }}>
          {row.first_name || 'NA'}
        </span>
      )
    },
    {
      name: 'Last Name',
      selector: row => row.last_name,
      sortable: true,
      minWidth: '160px',
      cell: row => (
        <span style={{ fontWeight: '600', color: '#495057' }}>
          {row.last_name || 'NA'}
        </span>
      )
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      minWidth: '160px',
      cell: row => (
        <span style={{ fontWeight: '600', color: '#495057' }}>
          {row.email || 'NA'}
        </span>
      )
    },
    {
      name: 'Amount',
      selector: row =>
        row.amount
          ? row.amount
          : 'NA',
      sortable: true,
      minWidth: '200px',
    },
    {
      name: 'Currency',
      selector: row =>
        row.currency
          ? row.currency
          : 'NA',
      sortable: true,
      minWidth: '100px',
    },
    {
      name: 'Earning Type',
      selector: row =>
        row.earning_type
          ? row.earning_type
          : 'NA',
      sortable: true,
      minWidth: '100px',
    },
    {
      name: 'Payment Method',
      selector: row =>
        row.payment_method
          ? row.payment_method
          : 'NA',
      sortable: true,
      minWidth: '100px',
    },
    {
      name: 'Created Date',
      selector: row =>
        row.createdAt
          ? moment(row.createdAt).local().format('DD MMM YYYY, hh:mm A')
          : 'NA',
      sortable: true,
      minWidth: '200px',
    },
    {
      name: 'Action',
      width: '120px',
      center: true,
      cell: row => (
        <div className="d-flex gap-2 justify-content-center">
          {hasPermission('bookings', 'view') && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="View"
              onClick={() => handleView(row.id)}
              style={{
                width: '32px',
                height: '32px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #dee2e6',
                borderRadius: '4px'
              }}
            >
              <i className="fe-eye" style={{ fontSize: '14px' }} />
            </button>
          )}

          {/* {hasPermission('bookings', 'delete') && row.is_deleted !== 1 && (
            <button
              className="btn btn-sm btn-outline-danger"
              title="Delete"
              onClick={() => handleDelete(row.id)}
              style={{
                width: '32px',
                height: '32px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #dc3545',
                borderRadius: '4px'
              }}
            >
              <i className="fe-trash-2" style={{ fontSize: '14px' }} />
            </button>
          )} */}
        </div>
      ),
    },
  ]

  const customStyles = {
    table: {
      style: {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6',
        minHeight: '48px',
      },
    },
    headCells: {
      style: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#495057',
        paddingLeft: '12px',
        paddingRight: '12px',
      },
    },
    cells: {
      style: {
        fontSize: '13px',
        color: '#212529',
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '12px',
        paddingRight: '12px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
        },
      },
      stripedStyle: {
        backgroundColor: '#ffffff',
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #dee2e6',
        minHeight: '56px',
        fontSize: '13px',
      },
    },
  }

  const handleDownloadExcel = async () => {
    try {
      const request = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchDebounce || undefined,
        is_report: true,
      };

      if (filters === 'custom' && filters?.rangDate?.start_date && filters?.rangDate?.end_date) {
        request.date_from = moment(filters.rangDate.start_date).format('YYYY-MM-DD');
        request.date_to = moment(filters.rangDate.end_date).format('YYYY-MM-DD');
      }

      const rData = await downloadEarningReport(request);

      if (Number(rData.code) === 200) {

        const columns = [
          { header: 'ID', key: 'id', width: 10, horizontal: 'center' },
          { header: 'Earning Type', key: 'earning_type', width: 18, horizontal: 'center' },

          // User Info
          { header: 'User Name', key: 'user_name', width: 25, horizontal: 'center' },
          { header: 'User Email', key: 'user_email', width: 30, horizontal: 'center' },

          // Payment Info
          { header: 'Amount', key: 'amount', width: 15, horizontal: 'center' },
          { header: 'Currency', key: 'currency', width: 10, horizontal: 'center' },
          { header: 'Payment Method', key: 'payment_method', width: 20, horizontal: 'center' },
          { header: 'Payment Status', key: 'payment_status', width: 18, horizontal: 'center' },

          // Dates
          { header: 'Purchase Date', key: 'purchase_date', width: 20, horizontal: 'center' },
          { header: 'Expiry Date', key: 'expiry_date', width: 20, horizontal: 'center' },

          // Subscription
          { header: 'Plan Name', key: 'plan_name', width: 20, horizontal: 'center' },
          { header: 'Plan Price', key: 'plan_price', width: 15, horizontal: 'center' },
          { header: 'Plan Duration', key: 'plan_duration', width: 15, horizontal: 'center' },

          // Transaction
          { header: 'Cart ID', key: 'cart_id', width: 20, horizontal: 'center' },
          { header: 'Transaction Total', key: 'transaction_total', width: 18, horizontal: 'center' },
          { header: 'VAT', key: 'vat', width: 15, horizontal: 'center' },
          { header: 'Commission Percentage', key: 'commision_percentage', width: 22, horizontal: 'center' },
          { header: 'Telr Percentage', key: 'telr_percentage', width: 20, horizontal: 'center' },
          { header: 'Telr Fixed', key: 'telr_fixed', width: 15, horizontal: 'center' },
          { header: 'Transaction Fee', key: 'transaction_fee', width: 18, horizontal: 'center' },
          { header: 'Admin Fee', key: 'admin_fee', width: 15, horizontal: 'center' },
          { header: 'Net Amount', key: 'net_amount', width: 18, horizontal: 'center' },

          // Booking
          { header: 'Order ID', key: 'order_id', width: 18, horizontal: 'center' },
          { header: 'Session Type', key: 'session_type', width: 18, horizontal: 'center' },
          { header: 'Total Sessions', key: 'total_sessions', width: 18, horizontal: 'center' },
          { header: 'Price Per Session', key: 'price_per_session', width: 20, horizontal: 'center' },
          { header: 'Booking Status', key: 'booking_status', width: 18, horizontal: 'center' },

          // Timestamps
          { header: 'Created At', key: 'createdAt', width: 20, horizontal: 'center' },
          { header: 'Updated At', key: 'updatedAt', width: 20, horizontal: 'center' },
        ];

        const ExportData = rData.data.map((row) => ({
          id: row.id || '-',
          earning_type: row.earning_type
            ? row.earning_type.replace(/_/g, ' ').toUpperCase()
            : '-',

          user_name: row.user_name || '-',
          user_email: row.user_email || '-',

          amount: row.amount ?? '-',
          currency: row.currency || '-',

          payment_method: row.payment_method
            ? getCardName(row.payment_method?.toUpperCase()) // 👈 use your function here
            : '-',

          payment_status: row.payment_status
            ? row.payment_status.charAt(0).toUpperCase() + row.payment_status.slice(1)
            : '-',

          purchase_date: row.purchase_date
            ? moment(row.purchase_date).format('YYYY-MM-DD HH:mm')
            : '-',

          expiry_date: row.expiry_date !== 'N/A' && row.expiry_date
            ? moment(row.expiry_date).format('YYYY-MM-DD')
            : '-',

          // Subscription
          plan_name: row.plan_name || '-',
          plan_price: row.plan_price ?? '-',
          plan_duration: row.plan_duration || '-',

          // Transaction
          cart_id: row.cart_id || '-',
          transaction_total: row.transaction_total ?? '-',
          vat: row.vat !== 'N/A' && row.vat !== undefined ? row.vat : '-',
          commision_percentage: row.commision_percentage !== 'N/A' && row.commision_percentage !== undefined ? `${row.commision_percentage}%` : '-',
          telr_percentage: row.telr_percentage !== 'N/A' && row.telr_percentage !== undefined ? `${row.telr_percentage}%` : '-',
          telr_fixed: row.telr_fixed !== 'N/A' && row.telr_fixed !== undefined ? row.telr_fixed : '-',
          transaction_fee: row.transaction_fee ?? '-',
          admin_fee: row.admin_fee ?? '-',
          net_amount: row.net_amount ?? '-',

          // Booking
          order_id: row.order_id || '-',
          session_type: row.session_type
            ? row.session_type.replace(/_/g, ' ').toUpperCase()
            : '-',

          total_sessions: row.total_sessions ?? '-',
          price_per_session: row.price_per_session ?? '-',

          booking_status: row.booking_status
            ? row.booking_status.replace(/_/g, ' ').toUpperCase()
            : '-',

          createdAt: row.createdAt
            ? moment(row.createdAt).format('YYYY-MM-DD HH:mm')
            : '-',

          updatedAt: row.updatedAt
            ? moment(row.updatedAt).format('YYYY-MM-DD HH:mm')
            : '-',
        }));

        const fileName = `Earning_Report_${moment().format('DDMMYYYYHmmss')}`;
        exportExcelFile(columns, ExportData, fileName);

        Swal.fire({
          icon: 'success',
          title: 'Downloaded',
          text: 'Earning report downloaded successfully',
          timer: 1500,
          showConfirmButton: false,
        });

      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: rData?.message });
      }

    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Failed', text: 'Something went wrong!' });
    }
  };


  return (
    <div className="container-fluid px-4 py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0 fw-semibold">Earning</h4>
      </div>

      {/* Search and Filter */}
      <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
        <div className="card-body p-3">
          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">

            {/* Left: Search */}
            <div className="position-relative">
              <i
                className="fe-search position-absolute"
                style={{
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6c757d',
                  fontSize: '16px'
                }}
              />
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  paddingLeft: '38px',
                  paddingRight: searchQuery ? '38px' : '12px',
                  height: '40px',
                  minWidth: '400px',
                  fontSize: '14px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px'
                }}
              />
              {searchQuery && (
                <button
                  className="btn btn-link position-absolute p-0"
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                >
                  <i className="fe-x" style={{ fontSize: '16px' }}></i>
                </button>
              )}
            </div>

            {/* Right: Filter dropdown + Download Report */}
            <div className="d-flex align-items-center gap-2">

              {/* Status Filter */}
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setPage(1)
                }}
                style={{
                  height: '40px',
                  width: '130px',
                  fontSize: '13px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px'
                }}
              >
                <option value="">All Status</option>
                <option value="subscription">From Tutor Subscriptions</option>
                <option value="session">From Parent Booking</option>
              </select>

              {/* Date Range Picker for report download */}
              <DateRangePicker
                onApply={setDates}
                initialSettings={{
                  startDate: moment().subtract(6, 'days').format('MM/DD/YYYY'),
                  endDate: moment().format('MM/DD/YYYY'),
                  ranges: Globals.datePickerRanges,
                  autoUpdateInput: false,
                }}
              >
                <input
                  type="text"
                  readOnly
                  value={
                    filters?.rangDate.start_date !== '' && filters?.rangDate.end_date !== ''
                      ? moment(filters?.rangDate.start_date).format('MMM DD, YYYY') + ' – ' + moment(filters?.rangDate.end_date).format('MMM DD, YYYY')
                      : 'Select Date Range'
                  }
                  className="form-control"
                  style={{
                    height: '40px',
                    width: '190px',
                    fontSize: '13px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    color: filters?.rangDate.start_date ? '#212529' : '#6c757d',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                />
              </DateRangePicker>


              {/* Clear Filters Button */}
              {isAnyFilterActive && (
                <button
                  type="button"
                  className="btn btn-outline-danger d-flex align-items-center gap-1 reset-filter-btn"
                  onClick={handleResetFilters}
                  title="Clear all filters"
                  style={{
                    height: '40px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                    flexShrink: 0,
                    border: '1px solid #dee2e6',
                    color: '#6c757d',
                  }}
                >
                  <i className="fe-x" style={{ fontSize: '14px' }} />
                  Clear Filters
                </button>
              )}

              {/* Download Report Button */}
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleDownloadExcel}
                disabled={isLoading || earningList?.data?.length === 0}
                style={{
                  height: '40px',
                  fontSize: '13px',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  paddingLeft: '14px',
                  paddingRight: '14px',
                  flexShrink: 0,
                }}
              >
                {isLoading && earningList?.data?.length > 0 ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-download" style={{ fontSize: '16px' }} />
                    Download Report
                  </>
                )}
              </button>

            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
        <DataTable
          columns={columns}
          data={earningList?.data || []}
          pagination
          paginationServer
          paginationTotalRows={earningList?.paging?.total || 0}
          paginationPerPage={limit}
          paginationDefaultPage={earningList?.paging?.page_no || 1}
          onChangeRowsPerPage={(newLimit) => {
            setLimit(newLimit)
            setPage(1)
          }}
          onChangePage={(newPage) => setPage(newPage)}
          responsive
          highlightOnHover
          persistTableHead
          progressPending={earningList?.loading}
          noDataComponent={
            <div className="p-5 text-center">
              <i className="fe-calendar" style={{ fontSize: '48px', color: '#adb5bd' }}></i>
              <h6 className="mt-3 mb-1 text-muted">
                {searchQuery ? 'No bookings found matching your search' : 'No bookings found'}
              </h6>
              <p className="text-muted small mb-0">
                {searchQuery ? 'Try adjusting your search criteria' : 'Bookings will appear here once created'}
              </p>
            </div>
          }
          customStyles={customStyles}

        />
      </div>
    </div>
  )
}

export default Earnings;